import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Outlet, useOutletContext } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useBranding } from '../lib/branding.jsx';
import { downloadAll, triggerDownload } from '../lib/downloadAll.js';
import Unlock from './Unlock.jsx';
import { DownloadSheet, BulkToast } from './DownloadUI.jsx';

/**
 * The gallery is now three pages — a cover, a film page, and a photos page —
 * because a wedding here means several films and up to ~600 photos, which is far
 * too much for one scrolling page. This layout is the shared spine: it loads the
 * gallery once, handles the password gate once, and owns the download machinery,
 * then hands everything to whichever child page is showing via router context.
 * The unlock cookie covers every sub-route, so a client types the password once.
 */
export default function GalleryLayout() {
  const { slug } = useParams();
  const { branding } = useBranding();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | locked | open | error
  const [error, setError] = useState('');

  // Download state lives here so the sheet works identically from either page.
  const [sheet, setSheet] = useState(false);
  const [bulk, setBulk] = useState(null);

  /**
   * Deter casual saving of the photographs: no right-click menu and no
   * drag-to-desktop on any image in the client gallery.
   *
   * Be clear-eyed about what this is — a deterrent, not protection. Screenshots,
   * dev tools and the network tab all still work, and nothing served to a
   * browser can ever truly be un-saveable. What it does buy is real though: the
   * sanctioned way to keep a photo becomes the Download button, which honours
   * the gallery's allow_photo_download setting, hands over the full-resolution
   * original rather than the compressed preview, and is recorded in
   * download_logs. Without this, "downloads off" was trivially bypassed by a
   * right-click that also silently gave the client the *worse* file.
   *
   * Scoped to the client gallery only: this layout never wraps the admin, whose
   * thumbnails must stay draggable for reordering.
   */
  useEffect(() => {
    const block = (e) => {
      if (e.target instanceof HTMLImageElement) e.preventDefault();
    };
    document.addEventListener('contextmenu', block);
    document.addEventListener('dragstart', block);
    return () => {
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('dragstart', block);
    };
  }, []);

  const loadAssets = useCallback(async () => {
    try {
      setData(await api.get(`/api/gallery/${slug}/assets`));
      setState('open');
    } catch (err) {
      if (err.status === 401) setState('locked');
      else {
        setError(err.message);
        setState('error');
      }
    }
  }, [slug]);

  useEffect(() => {
    (async () => {
      try {
        const m = await api.get(`/api/gallery/${slug}/meta`);
        setMeta(m);
        if (m.expired) {
          setError('This gallery has expired. Get in touch and we can open it again for you.');
          setState('error');
          return;
        }
        await loadAssets();
      } catch (err) {
        setError(err.message);
        setState('error');
      }
    })();
  }, [slug, loadAssets]);

  const photos = useMemo(() => (data ? data.assets.filter((a) => a.kind === 'photo') : []), [data]);
  const videos = useMemo(() => (data ? data.assets.filter((a) => a.kind === 'video') : []), [data]);

  /**
   * One file, saved the best way the device allows.
   *
   * A plain <a download> can only ever reach the browser's downloads folder —
   * on an iPhone that is the Files app, not Photos, which is not where anyone
   * goes looking for their wedding pictures. The Web Share API is the only
   * route a website has into the camera roll: hand the actual file to the OS
   * share sheet and it offers "Save Image", which writes straight to Photos.
   *
   * Photos only. Films are multi-GB and must never be pulled through the JS
   * heap — they keep the straight-to-download-manager path.
   *
   * Anything that goes wrong falls through to that same plain download,
   * including Safari refusing the share because fetching the original outlived
   * the tap's user activation. Worse placement beats no file.
   */
  const downloadOne = useCallback(
    async (asset) => {
      const url = `/api/gallery/${slug}/assets/${asset.id}/download`;

      if (asset.kind === 'photo' && navigator.canShare) {
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error('Could not fetch the original');
          const blob = await res.blob();
          const file = new File([blob], asset.filename, { type: blob.type || 'image/jpeg' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file] });
            return;
          }
        } catch (err) {
          // Dismissing the share sheet is a decision, not a failure — don't
          // then push the file into downloads behind their back.
          if (err?.name === 'AbortError') return;
        }
      }

      triggerDownload(url, asset.filename);
    },
    [slug],
  );

  const startDownloadAll = useCallback(
    async (kind /* 'all' | 'photo' | 'video' */) => {
      setSheet(false);
      setBulk({ triggered: 0, total: 0, phase: 'preparing' });
      try {
        const { files, totalBytes } = await api.get(`/api/gallery/${slug}/download-manifest`);
        const chosen = kind === 'all' ? files : files.filter((f) => f.kind === kind);
        setBulk({ triggered: 0, total: chosen.length, totalBytes, phase: 'running' });
        await downloadAll(chosen, { onProgress: ({ triggered, total }) => setBulk((b) => ({ ...b, triggered, total })) });
        setBulk((b) => ({ ...b, phase: 'done' }));
        setTimeout(() => setBulk(null), 12000);
      } catch (err) {
        setBulk({ phase: 'error', error: err.message });
      }
    },
    [slug],
  );

  if (state === 'loading') return <LoadingVeil />;
  if (state === 'error')
    return (
      <div className="centered">
        <div className="card narrow" style={{ textAlign: 'center' }}>
          <span className="label">Gallery</span>
          <h1 className="serif" style={{ margin: '0.75rem 0 0.5rem' }}>Unavailable</h1>
          <p className="muted">{error}</p>
          {branding?.contact_email && <a href={`mailto:${branding.contact_email}`}>{branding.contact_email}</a>}
        </div>
      </div>
    );
  if (state === 'locked') return <Unlock meta={meta} slug={slug} coverUrl={meta?.cover_url} onUnlocked={loadAssets} />;

  const ctx = {
    slug,
    branding,
    gallery: data.gallery,
    assets: data.assets,
    photos,
    videos,
    // Only sections holding photographs — the server already filters empties out.
    sections: data.sections || [],
    downloadOne,
    openDownload: () => setSheet(true),
    downloadsOn: data.gallery.allow_photo_download || data.gallery.allow_video_download,
  };

  return (
    <>
      <Outlet context={ctx} />
      {sheet && (
        <DownloadSheet
          gallery={data.gallery}
          photos={photos}
          videos={videos}
          onClose={() => setSheet(false)}
          onChoose={startDownloadAll}
        />
      )}
      {bulk && <BulkToast bulk={bulk} onClose={() => setBulk(null)} />}
    </>
  );
}

export const useGallery = () => useOutletContext();

function LoadingVeil() {
  return (
    <div className="centered" style={{ background: '#221e1a' }}>
      <span className="label on-dark" style={{ animation: 'shimmer 1.5s infinite' }}>Loading</span>
    </div>
  );
}
